import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { menuPermissions, roleLabels, MenuItemWithSub } from "../types/User";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarFooter,
  useSidebar,
} from "./ui/sidebar";
import { ChevronRight, User, BarChart3 } from "lucide-react";
import { cn } from "../lib/utils";

export function AppSidebar() {
  const location = useLocation();
  const { user, hasPermission, centerInfo } = useAuth();
  const { state } = useSidebar();
  const [expandedMenus, setExpandedMenus] = React.useState<string[]>([]);

  const menuItems = menuPermissions.filter(
    (item) => user && hasPermission(item.allowedRoles) && item.path !== '/analytics'
  ).map(item => {
    // Always fix statistics icon to ensure it's never a string
    if (item.path === '/statistics') {
      return { ...item, icon: BarChart3 };
    }
    return item;
  });

  const toggleSubmenu = (path: string) => {
    setExpandedMenus((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const isSubMenuActive = (item: MenuItemWithSub) => {
    return (
      item.subItems?.some((subItem) => location.pathname === subItem.path) ||
      false
    );
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b h-16">
        <div className={cn(
          "flex items-center px-2 h-full",
          state !== "collapsed" ? "gap-3" : "justify-center"
        )}>
          <img
            src="/Matrixcloud_logo.png"
            alt="Matrixcloud"
            className="h-8 w-8 shrink-0 object-contain"
          />
          {state !== "collapsed" && (
            <span className="font-semibold truncate">Matrix Survey</span>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* User Info Section */}
        {user && (
          <SidebarGroup>
            <SidebarGroupLabel>사용자 정보</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={`${user.name} (${roleLabels[user.role]})`}
                    className="pointer-events-none h-auto py-3"
                  >
                    <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                      <User className="h-4 w-4" />
                    </div>
                    {state !== "collapsed" && (
                      <div className="flex flex-col min-w-0 gap-0.5">
                        <span className="text-sm font-medium truncate leading-tight">
                          {user.name}
                        </span>
                        <span className="text-xs text-muted-foreground truncate leading-tight">
                          {roleLabels[user.role]}
                        </span>
                        {centerInfo && (
                          <span className="text-xs text-muted-foreground truncate leading-tight">
                            {centerInfo.centerName}
                          </span>
                        )}
                      </div>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Navigation Menu */}
        <SidebarGroup>
          <SidebarGroupLabel>메뉴</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  {item.subItems && item.subItems.length > 0 ? (
                    <>
                      <SidebarMenuButton
                        onClick={() => toggleSubmenu(item.path)}
                        isActive={isSubMenuActive(item)}
                        tooltip={item.label}
                      >
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                          {React.createElement(item.icon, { size: 16 })}
                        </div>
                        {state !== "collapsed" && <span>{item.label}</span>}
                        {state !== "collapsed" && (
                          <ChevronRight
                            className={cn(
                              "ml-auto h-4 w-4 transition-transform",
                              expandedMenus.includes(item.path) && "rotate-90"
                            )}
                          />
                        )}
                      </SidebarMenuButton>

                      {expandedMenus.includes(item.path) && (
                        <SidebarMenuSub>
                          {item.subItems
                            .filter(
                              (subItem) =>
                                user && hasPermission(subItem.allowedRoles)
                            )
                            .map((subItem) => (
                              <SidebarMenuSubItem key={subItem.path}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={location.pathname === subItem.path}
                                >
                                  <Link to={subItem.path}>
                                    <span>{subItem.label}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                        </SidebarMenuSub>
                      )}
                    </>
                  ) : (
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === item.path}
                      tooltip={item.label}
                    >
                      <Link to={item.path}>
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                          {React.createElement(item.icon, { size: 16 })}
                        </div>
                        {state !== "collapsed" && <span>{item.label}</span>}
                      </Link>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t">
        <div className="px-2 py-2 text-xs text-muted-foreground">
          © 2025 MatrixCloud
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
